
import React, { useState, useEffect } from "react";
import Sidebar from "./sidebar/Sidebar";
import SubPage from "./SubPage";
import "./CategoriesPage.css";
import Breadcrumbs from "../components/Breadcrumbs";
import axios from "axios";
import { useLocation } from "react-router-dom";
import apiService from "../utils/api";

const getUserRole = () => (sessionStorage.getItem("user_role") || "").replace(/\s+/g, '');

const buildHierarchy = (nodes) => {
  return nodes.map(node => {
    const isFolder = node.data.type === 'Folder';
    return {
      key: node.key,
      data: {
        ...node.data,
        type: isFolder ? 'Folder' : 'File',
        name: node.data.name || node.data.contract_title || 'N/A',
        clientName: node.data.clientName || 'N/A',
        createdDate: node.data.createdDate || node.data.created_at || 'N/A',
        status: node.data.status || 'N/A',
        modified: node.data.modified || node.data.updated_at || 'N/A',
        contractType: node.data.contract_type_name || 'N/A',
        creationType: node.data.creation_type || 'N/A',
        id: node.data.id || node.data.contract_id || 'N/A',
        is_favourite: node.data.is_favourite || false,
        is_deleted: node.data.is_deleted || false,
      },
      children: node.children?.length ? buildHierarchy(node.children) : [],
    };
  });
};
const parseLegalHierarchy = (hierarchy) => buildHierarchy(hierarchy);
const parseAdminHierarchy = (hierarchy) => buildHierarchy(hierarchy);

const TreeNode = ({ node, level = 0, onSelect, collapsed = false, isSelected = false, expandedKeys, selectedNode }) => {
  const [open, setOpen] = useState(!!expandedKeys[node.key]);
  
  useEffect(() => {
    setOpen(!!expandedKeys[node.key]);
  }, [expandedKeys, node.key]);

  const toggle = (e) => {
    e.stopPropagation();
    setOpen(!open);
  };

  const handleSelect = (e) => {
    e.stopPropagation();
    if (node.children && node.children.length > 0) {
      toggle(e);
    } else {
      onSelect({ id: node.data.id, data: node.data,role: getUserRole() });
    }
  };

  return (
    <div
      className="tree-node"
      style={{ marginLeft: collapsed ? 0 : `${level * 20}px` }}
    >
      <div
        className={`categories-section-tree-node-header ${
          node.children && node.children.length > 0 ? "categories-section-folder" : "categories-section-document"
        } ${isSelected ? "categories-section-tree-node-header--highlighted" : ""}`}
        onClick={handleSelect}
        onKeyDown={(e) => e.key === "Enter" && handleSelect(e)}
        tabIndex={0}
        role="button"
      >
        {node.children && node.children.length > 0 && (
          <span className="toggle-icon">{open ? "▼" : "▶"}</span>
        )}
        <span>{node.children && node.children.length > 0 ? "📂" : "📄"}</span>
        {!collapsed && <span> {node.data?.name || node.name}</span>}
      </div>

      {open && node.children && (
        <div className="node-children">
          {node.children.map((child) => (
            <TreeNode
              key={child.key || child.id}
              node={child}
              level={level + 1}
              onSelect={onSelect}
              collapsed={collapsed}
              expandedKeys={expandedKeys}
              isSelected={child.data?.id === selectedNode?.id}
              selectedNode={selectedNode}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CategoriesPage = () => {
  const location = useLocation();
  const contractId = location.state?.contractId || null;
  const clientKey = location.state?.clientKey || null;
 const fromArchive = location.state?.fromArchive || false;

  
  const [selectedNode, setSelectedNode] = useState(() => {
  if (contractId) {
    return { id: `CONTRACT-${contractId}`, data: { type: "File", id: `CONTRACT-${contractId}` } };
  }
  return null;
});
  const [isTreeCollapsed, setIsTreeCollapsed] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [filteredNodes, setFilteredNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [searchTerm, setSearchTerm] = useState("");


const filterNodes = (nodes, searchTerm) => {
  if (!searchTerm) return nodes;
  
  const filterRecursive = (nodeList) => {
    return nodeList.reduce((acc, node) => {
      const matchesSearch = node.data?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           node.data?.contract_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           node.data?.clientName?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const filteredChildren = node.children ? filterRecursive(node.children) : [];
      
      if (matchesSearch || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren
        });
      }
      
      return acc;
    }, []);
  };
  
  return filterRecursive(nodes);
};

const handleSearch = (term) => {
  setSearchTerm(term);
  const filtered = filterNodes(nodes, term);
  setFilteredNodes(filtered);
};

const fetchContractById = async (contractId, fromWipPage) => {
  const endpoint = fromWipPage 
    ? `contracts/${contractId}?is_wip=true`
    : `contracts/${contractId}`;
  const res = await apiService.fetchData(endpoint);
  if (res && res.contract_title) {
    return [
      {
        id: `CONTRACT-${contractId}`,
        data: {
          id: `CONTRACT-${contractId}`,
          type: "File",
          name: res.contract_title,
          contract_title: res.contract_title,
        },
      },
    ];
  }
  return [];
};

const updateContractNameInHierarchy = (nodes, contractId) => {
  return nodes.map((node) => {
    if (node.children?.length) {
      const hasTargetChild = node.children.some(
        (child) =>
          child.data?.id === `CONTRACT-${contractId}` ||
          child.data?.contract_id?.toString() === contractId.toString()
      );

      const updatedChildren = updateContractNameInHierarchy(node.children, contractId);
      if (hasTargetChild) {
        const targetChild = node.children.find(
          (child) =>
            child.data?.id === `CONTRACT-${contractId}` ||
            child.data?.contract_id?.toString() === contractId.toString()
        );
        return {
          ...node,
          data: {
            ...node.data,
            name:
              targetChild?.data?.contract_title ||
              targetChild?.data?.name ||
              node.data?.name,
          },
          children: updatedChildren,
        };
      }
      return { ...node, children: updatedChildren };
    }
    return node;
  });
};

const findParentNode = (nodes, targetId) => {
  for (const node of nodes) {
    if (
      node.data?.id?.endsWith(`-${targetId}`) ||
      node.data?.id === targetId ||
      node.data?.id === `cont_${targetId}` ||
      node.data?.id === `CONTRACT-${targetId}`
    ) {
      return node;
    }
    if (node.children?.length) {
      const found = findParentNode(node.children, targetId);
      if (found) return node;
    }
  }
  return null;
};

const getEndpoint = (role, userId, fromWipPage) => {
  const isWipParam = fromWipPage ? "&is_wip=true" : "&is_wip=false";
  return role === "Admin"
    ? `all-contracts-hierarchy?user_id=${userId}${isWipParam}`
    : `contract-hierarchy?user_id=${userId}${isWipParam}`;
};

const parseHierarchyByRole = (role, hierarchy) => {
  return role === "Admin"
    ? parseAdminHierarchy(hierarchy)
    : parseLegalHierarchy(hierarchy);
};

const handleEmptyHierarchy = () => {
  console.warn(" Empty hierarchy data returned from API");
  setNodes([]);
};

const handleContractHierarchyCases = async (
  parsedHierarchy,
  contractId,
  clientKey,
  fromDataTable,
  fromTreeTable,
  fromWipPage
) => {
  if (fromDataTable && contractId) {
    const result = await fetchContractById(contractId, fromWipPage);
    setNodes(result);
    setLoading(false);
    return true; // handled
  }

  if (fromTreeTable && contractId) {
    const fixedHierarchy = updateContractNameInHierarchy(parsedHierarchy, contractId);
    setNodes(fixedHierarchy);
    return true; // handled
  }

  if (clientKey || contractId) {
    const clientNode = findParentNode(parsedHierarchy, contractId);
    setNodes(clientNode ? [clientNode] : []);
    return true;
  }

  setNodes(parsedHierarchy);
  return false;
};

useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);
      const userId = sessionStorage.getItem("user_id") || "";
      const role = location.state?.role || getUserRole();

      console.log("🔹 User ID:", userId);
      console.log("🔹 Role:", role);
      console.log("🔹 Location state:", location.state);

      const fromWipPage = location.state?.fromWipPage || false;
      const isWipParam = fromWipPage ? "&is_wip=true" : "&is_wip=false";
      
      // const endpoint =
      //   role === "Admin"
      //     ? `all-contracts-hierarchy?user_id=${userId}${isWipParam}`
      //     : `contract-hierarchy?user_id=${userId}${isWipParam}`;
      const endpoint = getEndpoint(role, userId, fromWipPage);


      console.log(" Fetching from endpoint:", endpoint);
      console.log(" From WIP Page:", fromWipPage);

      const res = await apiService.fetchData(endpoint);
      console.log("API response:", res);

      if (res && (res.contract_hierarchy || res.data)) {
        const hierarchy = res.contract_hierarchy || res.data;
        const fromDataTable = location.state?.fromDataTable || false;
        const fromTreeTable = location.state?.fromTreeTable || false;

        console.log(" fromDataTable:", fromDataTable);
        console.log(" fromTreeTable:", fromTreeTable);
        console.log(" contractId:", contractId);
        console.log(" clientKey:", clientKey);

        const parsedHierarchy = parseHierarchyByRole(role, hierarchy);
        console.log(" Parsed hierarchy (top-level):", parsedHierarchy?.[0]);

await handleContractHierarchyCases(
  parsedHierarchy,
  contractId,
  clientKey,
  fromDataTable,
  fromTreeTable,
  fromWipPage
);

      } else {
        // console.warn(" Empty hierarchy data returned from API");
        // setNodes([]);
        handleEmptyHierarchy();
      }
    } catch (error) {
      console.error(" Error fetching contract hierarchy:", error);
      setNodes([]);
    } finally {
      console.log("Finished fetchData");
      setLoading(false);
    }
  };

  fetchData();
}, [contractId, clientKey, location.state?.role]);

// Update filtered nodes when nodes change
useEffect(() => {
  const filtered = filterNodes(nodes, searchTerm);
  setFilteredNodes(filtered);
}, [nodes, searchTerm]);



useEffect(() => {
  if (contractId && nodes.length > 0) {
    const newExpandedKeys = {};
    const findPath = (nodes, targetId, path = []) => {
      for (const node of nodes) {
        if (
          node.data?.id?.endsWith(`-${targetId}`) ||
          node.data?.id === targetId ||
          node.data?.id === `cont_${targetId}` ||
          node.data?.id === `CONTRACT-${targetId}`
        ) {
          path.forEach((key) => (newExpandedKeys[key] = true));
          return true;
        }
        if (node.children) {
          if (findPath(node.children, targetId, [...path, node.key])) {
            newExpandedKeys[node.key] = true;
            return true;
          }
        }
      }
      return false;
    };
    findPath(nodes, contractId);
    setExpandedKeys(newExpandedKeys);
  }
}, [contractId, nodes]);

  const treeWidth = isTreeCollapsed ? 64 : 360;
  const treeStyle = {
    width: `${treeWidth}px`,
    minWidth: `${treeWidth}px`,
    transition: "width 0.25s ease",
    overflow: "hidden",
    borderRight: "1px solid #eee",
    paddingRight: isTreeCollapsed ? "8px" : "12px",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };

  const subpageStyle = {
    flex: 1,
    paddingLeft: isTreeCollapsed ? "12px" : "16px",
  };

  return (
    <div className="categories-page-container">
      <Sidebar />
      <div className="categories-page-content">
      <Breadcrumbs /><br/>
        <h1 style={{ color: "#ff6b35", fontSize: "24px" }} className="title">File Explorer</h1>
        
        {/* Search Box */}
        <div style={{ marginBottom: "16px" }}>
          <input
            type="text"
            placeholder="Search contracts..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            style={{
              width: "300px",
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "14px"
            }}
          />
        </div>
        
        <div className="big-card" style={{ display: "flex" }}>
          <div className="categories-tree" style={treeStyle}>
  <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
    <button
      onClick={() => setIsTreeCollapsed((v) => !v)}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        width: "35px",
        height: "35px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      aria-label="Toggle categories panel"
      title="Toggle categories panel"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#374151"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          transition: "transform 0.25s ease",
          transform: isTreeCollapsed ? "rotate(45deg)" : "none",
        }}
      >
        {isTreeCollapsed ? (
          <path d="M18 6L6 18M6 6l12 12" />
        ) : (
          <path d="M3 12h18M3 6h18M3 18h18" />
        )}
      </svg>
    </button>
  </div>


  <div
    style={{
      overflowX: "auto",         
      overflowY: "auto",         
      maxHeight: "80vh",         
      width: "100%",
      whiteSpace: "nowrap",      
      paddingBottom: "10px",
    }}
  >
    {loading ? (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <svg
          width="40"
          height="40"
          viewBox="0 0 50 50"
          xmlns="http://www.w3.org/2000/svg"
          style={{ animation: "spin 1s linear infinite" }}
        >
          <circle
            cx="25"
            cy="25"
            r="20"
            stroke="#ff6b35"
            strokeWidth="5"
            fill="none"
            strokeLinecap="round"
            style={{ strokeDasharray: "90 150", strokeDashoffset: "-35" }}
          />
          <style>
            {`
              @keyframes spin {
                100% {
                  transform: rotate(360deg);
                }
              }
            `}
          </style>
        </svg>
      </div>
    ) : (
      (searchTerm ? filteredNodes : nodes).map((node) => (
        <TreeNode
          key={node.key || node.id}
          node={node}
          onSelect={setSelectedNode}
          expandedKeys={expandedKeys}
          collapsed={isTreeCollapsed}
          //isSelected={node.data?.id === selectedNode?.id}
          isSelected={node.data?.id?.endsWith(`-${contractId}`) || node.data?.id === contractId}
        />
      ))
    )}
  </div>
</div>


          <div className="categories-subpage" style={subpageStyle}>
            <SubPage selectedNode={selectedNode} fromArchive={fromArchive} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;